import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useBooks } from '@/hooks/use-books';
import { useAppConfig } from '@/hooks/use-app-config';

interface AppReadinessState {
  isAppReady: boolean;
  isCriticalDataLoading: boolean;
  hasCriticalError: boolean;
  readinessDetails: {
    hasBooks: boolean;
    hasConfig: boolean;
    isBooksLoading: boolean;
    isConfigLoading: boolean;
  };
}

const AppReadinessContext = createContext<AppReadinessState | null>(null);

interface AppReadinessProviderProps {
  children: ReactNode;
}

/**
 * AppReadinessProvider tracks critical data loading state
 * to prevent rendering UI components before data is available.
 * This eliminates FOUC/layout thrashing on cold starts.
 */
export function AppReadinessProvider({ children }: AppReadinessProviderProps) {
  const { allBooks, isLoading: isBooksLoading, isError: isBooksError } = useBooks();
  const { data: appConfig, isLoading: isConfigLoading, isError: isConfigError } = useAppConfig();

  const readinessState = useMemo<AppReadinessState>(() => {
    const hasBooks = allBooks.length > 0;
    const hasConfig = !!appConfig;
    
    // Critical data is loading if either books or config are still loading
    const isCriticalDataLoading = isBooksLoading || isConfigLoading;
    
    // App is ready when we have at least books loaded (config can come later)
    // We're more lenient - don't block forever if config fails
    const isAppReady = hasBooks || (!isBooksLoading && !isBooksError);
    
    const hasCriticalError = isBooksError && isConfigError;

    return {
      isAppReady,
      isCriticalDataLoading,
      hasCriticalError,
      readinessDetails: {
        hasBooks,
        hasConfig,
        isBooksLoading,
        isConfigLoading,
      },
    };
  }, [allBooks, appConfig, isBooksLoading, isConfigLoading, isBooksError, isConfigError]);

  return (
    <AppReadinessContext.Provider value={readinessState}>
      {children}
    </AppReadinessContext.Provider>
  );
}

export function useAppReadiness(): AppReadinessState {
  const context = useContext(AppReadinessContext);
  if (!context) {
    // Return a safe default if used outside provider (graceful degradation)
    return {
      isAppReady: true,
      isCriticalDataLoading: false,
      hasCriticalError: false,
      readinessDetails: {
        hasBooks: true,
        hasConfig: true,
        isBooksLoading: false,
        isConfigLoading: false,
      },
    };
  }
  return context;
}
